/*
 * Cart drawer — SHIPPED 2026-10-15 (experiment "cart_drawer" decided:
 * Drawer launched for everyone; owner decision on a flat result).
 * The drawer is the default cart UX: ATC success opens it (Empire's
 * flyout banner suppressed), cart-icon click opens it. The /cart page
 * remains reachable (drawer's edit link, direct URL).
 *
 * Review escape hatch: ?preview_cart=page forces the old page-cart
 * behavior for that pageload (debugging / future experiments).
 *
 * ATC detection: both transports — Empire's PDP form adds via its
 * bundled jQuery (XHR), other surfaces (Doofinder search layer) via
 * fetch. Observation only; requests are untouched.
 *
 * Re-render: after any line change the section is re-fetched via the
 * Section Rendering API and swapped in. cart_open/cart_update events
 * keep logging to Statsig as the mechanism baseline for follow-up
 * cart experiments.
 */
(function () {
  'use strict';

  function resolveAssignment() {
    return /[?&]preview_cart=page/.test(location.search) ? 'page' : 'drawer';
  }

  function logEvent(name, value, meta) {
    try {
      if (window.statsig && typeof window.statsig.logEvent === 'function') {
        window.statsig.logEvent(name, value, meta);
      }
    } catch (e) {}
  }

  // ---------- drawer element ----------

  function el() { return document.getElementById('fe24-cart-drawer'); }

  var lastTrigger = null; // element to restore focus to on close (Dawn parity)

  function trapTab(e) {
    var d = el();
    if (!d || !d.classList.contains('cdw--open') || e.key !== 'Tab') return;
    var focusables = d.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openDrawer(trigger) {
    var d = el();
    if (!d) return;
    if (document.activeElement && document.activeElement !== document.body) {
      lastTrigger = document.activeElement;
    }
    // Force a style flush of the closed state (the element may have been
    // inserted this frame via replaceWith), THEN activate on the next
    // frame — otherwise the browser coalesces both states and the
    // transform never transitions (Dawn solves this the same way).
    var panel = d.querySelector('.cdw__panel');
    void (panel && panel.getBoundingClientRect());
    requestAnimationFrame(function () {
      d.classList.add('cdw--open');
      d.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cdw-open');
      document.body.style.overflow = 'hidden';
      var closeBtn = d.querySelector('.cdw__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      else if (panel) panel.focus({ preventScroll: true });
    });
    logEvent('cart_open', undefined, { ui: 'drawer', trigger: trigger || '' });
  }

  function closeDrawer() {
    var d = el();
    if (!d) return;
    d.classList.remove('cdw--open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cdw-open');
    document.body.style.overflow = '';
    if (lastTrigger && document.contains(lastTrigger)) {
      try { lastTrigger.focus({ preventScroll: true }); } catch (e) {}
    }
    lastTrigger = null;
  }

  var rerenderQueued = false;
  function rerender(thenOpen, trigger) {
    if (rerenderQueued) return;
    rerenderQueued = true;
    fetch('/?sections=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var fresh = tpl.content.querySelector('#fe24-cart-drawer');
        var current = el();
        if (fresh && current) {
          var wasOpen = current.classList.contains('cdw--open');
          var oldErr = current.querySelector('[data-cdw-error]');
          if (oldErr && oldErr.classList.contains('cdw__error--visible')) {
            var newErr = fresh.querySelector('[data-cdw-error]');
            if (newErr) {
              newErr.textContent = oldErr.textContent;
              newErr.classList.add('cdw__error--visible');
            }
          }
          current.replaceWith(fresh);
          if (wasOpen) {
            // stay open without re-running the slide-in
            fresh.classList.add('cdw--open');
            fresh.setAttribute('aria-hidden', 'false');
          } else if (thenOpen) {
            openDrawer(trigger);
          }
        }
        updateHeaderCount(fresh ? fresh.dataset.itemCount : null);
      })
      .catch(function () {})
      .finally(function () { rerenderQueued = false; });
  }

  function updateHeaderCount(count) {
    if (count == null) return;
    document.querySelectorAll('[data-header-cart-count]').forEach(function (n) {
      n.setAttribute('data-header-cart-count', count === '0' ? '' : count);
    });
  }

  // ---------- line updates ----------

  function showError(message) {
    var err = el() && el().querySelector('[data-cdw-error]');
    if (!err) return;
    err.textContent = message;
    err.classList.add('cdw__error--visible');
    clearTimeout(showError._t);
    showError._t = setTimeout(function () {
      err.classList.remove('cdw__error--visible');
    }, 5000);
  }

  var changePending = false;
  function changeLine(line, quantity) {
    if (changePending) return;
    changePending = true;
    var row = el() && el().querySelector('[data-cdw-line="' + line + '"]');
    if (row) row.classList.add('cdw--busy');
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) {
          // 422 etc: Shopify sends {message, description}
          showError(res.data && (res.data.description || res.data.message) || 'Fehler');
          return;
        }
        logEvent('cart_update', undefined, { ui: 'drawer' });
        // Shopify silently caps at available stock: surface it (Dawn parity)
        var item = res.data.items && res.data.items[line - 1];
        if (quantity > 0 && item && item.quantity < quantity) {
          var err = el() && el().querySelector('[data-cdw-error]');
          var tpl = err && err.dataset.msgLimited || 'Max: *count*';
          showError(tpl.replace('*count*', item.quantity));
        }
      })
      .catch(function () {})
      .finally(function () {
        changePending = false;
        rerender();
      });
  }

  // ---------- event wiring ----------

  document.addEventListener('click', function (e) {
    var d = el();
    if (d && d.classList.contains('cdw--open')) {
      if (e.target.closest('[data-cdw-close]')) { closeDrawer(); return; }
      var qty = e.target.closest('[data-cdw-qty]');
      if (qty) {
        var item = qty.closest('[data-cdw-line]');
        var line = parseInt(item.dataset.cdwLine, 10);
        var current = parseInt(item.querySelector('[data-cdw-qty-input]').value, 10) || 1;
        changeLine(line, Math.max(0, current + parseInt(qty.dataset.cdwQty, 10)));
        return;
      }
      var rm = e.target.closest('[data-cdw-remove]');
      if (rm) {
        changeLine(parseInt(rm.closest('[data-cdw-line]').dataset.cdwLine, 10), 0);
        return;
      }
      if (e.target.closest('[data-cdw-checkout]')) {
        logEvent('begin_checkout_click', undefined, { ui: 'drawer' });
        window.location.href = '/checkout';
        return;
      }
    }

    // Header cart icon: first cart interaction -> resolve arm.
    var cartLink = e.target.closest('a.site-header-cart--button, a[href$="/cart"]');
    if (cartLink && !e.target.closest('#fe24-cart-drawer')) {
      var ui = resolveAssignment();
      if (ui === 'drawer') {
        e.preventDefault();
        rerender(true, 'cart_icon');
      } else {
        logEvent('cart_open', undefined, { ui: 'page', trigger: 'cart_icon' });
      }
    }
  }, true);

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cdw-qty-input]');
    if (!input) return;
    var line = parseInt(input.closest('[data-cdw-line]').dataset.cdwLine, 10);
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) { rerender(); return; }
    changeLine(line, value);
  });

  document.addEventListener('keydown', function (e) {
    trapTab(e);
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key === 'Enter' && e.target.closest && e.target.closest('[data-cdw-qty-input]')) {
      e.preventDefault();
      e.target.blur(); // triggers the change handler
    }
  });

  // ---------- ATC observation ----------
  // BOTH transports: Empire's PDP form adds via its bundled jQuery (XHR),
  // but other surfaces add via fetch — notably the Doofinder search layer.
  // Observing only XHR missed those customers entirely (orders without
  // cart_drawer attribution). Same belt-and-braces as statsig.liquid.

  function onAtcComplete(ok) {
    if (ok) {
      var ui = resolveAssignment();
      if (ui === 'drawer') {
        document.documentElement.classList.add('cdw-suppress-banner');
        rerender(true, 'add_to_cart');
      } else {
        logEvent('cart_open', undefined, { ui: 'page', trigger: 'atc_banner' });
      }
    } else {
      // Failed add (sold out, stock limit): let Empire's banner show
      // the error even in the drawer arm.
      document.documentElement.classList.remove('cdw-suppress-banner');
    }
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
        this.addEventListener('load', function () {
          onAtcComplete(this.status >= 200 && this.status < 300);
        });
      }
    } catch (e) {}
    return origOpen.apply(this, arguments);
  };

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = '';
    try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
    var p = origFetch.apply(this, arguments);
    if (url.indexOf('/cart/add') !== -1) {
      p.then(function (res) { onAtcComplete(!!res && res.ok); }).catch(function () {});
    }
    return p;
  };

  // ---------- enrollment coverage beyond ATC/icon clicks ----------

  // Keep the cart-page view in the mechanism telemetry (funnel context
  // for follow-up cart experiments).
  if (/\/cart\/?$/.test(location.pathname)) {
    var logCartPage = function () {
      logEvent('cart_open', undefined, { ui: 'page', trigger: 'cart_page' });
    };
    if (document.readyState === 'complete') logCartPage();
    else window.addEventListener('load', logCartPage);
  }
})();
